// src/features/project-intake/components/CustomerSearchDropdown.tsx

import React, { useMemo } from 'react';
import { Customer, CustomerContactListItem } from '../../../types';

interface CustomerSearchDropdownProps {
  customers: Customer[];
  customerContacts: CustomerContactListItem[];
  searchValue: string;
  onSearchChange: (value: string) => void;
  onCustomerSelect: (customer: Customer) => void;
  showDropdown: boolean;
  onFocus: () => void;
  onBlur: () => void;
  error: string | null;
}

// Customer search input with dropdown
export const CustomerSearchDropdown = React.memo<CustomerSearchDropdownProps>(
  ({
    customers,
    customerContacts,
    searchValue,
    onSearchChange,
    onCustomerSelect,
    showDropdown,
    onFocus,
    onBlur,
    error,
  }) => {
    const filteredCustomers = useMemo(() => {
      if (!searchValue) return customers;
      return customers.filter((c) => c.item.toLowerCase().includes(searchValue.toLowerCase()));
    }, [customers, searchValue]);

    const getCustomerContacts = (customerId: number) => {
      return customerContacts.filter((contact) => contact.customer_id === customerId);
    };

    return (
      <div className="relative">
        <label className="block text-sm font-medium theme-text-secondary mb-1.5">
          Customer<span className="text-red-500 ml-1">*</span>
        </label>
        <input
          type="text"
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          onFocus={onFocus}
          onBlur={onBlur}
          placeholder="Search or select client"
          className={`w-full border rounded-lg px-3 py-2.5 text-sm theme-card theme-text-primary focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
            error ? 'border-red-500 ring-2 ring-red-900/20' : 'theme-border'
          }`}
        />
        {error && (
          <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
            <span>⚠</span> {error}
          </p>
        )}
        {showDropdown && (
          <div className="absolute z-10 w-full mt-1 theme-card border theme-border rounded-lg shadow-xl max-h-60 overflow-y-auto">
            {filteredCustomers.length > 0 ? (
              filteredCustomers.map((c) => {
                const contacts = getCustomerContacts(c.id);
                return (
                  <div
                    key={c.id}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onCustomerSelect(c);
                    }}
                    className="p-3 hover:bg-blue-600/10 cursor-pointer text-sm border-b theme-border last:border-b-0 transition-colors"
                  >
                    <div className="font-medium theme-text-primary">{c.item}</div>
                    {contacts.length > 0 && (
                      <div className="text-xs theme-text-tertiary mt-1.5 space-y-1">
                        {contacts.map((contact) => (
                          <div key={contact.id} className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium">{contact.name}</span>
                            {contact.email && <span>• {contact.email}</span>}
                            {contact.phone && <span>• {contact.phone}</span>}
                            {contact.is_primary && (
                              <span className="text-blue-500 font-medium">(Primary)</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="p-3 text-sm font-semibold theme-text-tertiary">No clients found</div>
            )}
          </div>
        )}
      </div>
    );
  }
);

CustomerSearchDropdown.displayName = 'CustomerSearchDropdown';
